import React from 'react'
import {
  Box,
  Stack,
  Text,
  VStack,
  Icon,
  HStack,
  Center,
  Link as ChakraLink
} from '@chakra-ui/react'
import { Link as ReactRouterLink, useLocation } from 'react-router-dom'
import {
  useGetCompanyInfoQuery,
  useCountRidersQuery,
  useCountTasksQuery,
  useCountTaskTypesQuery,
  useCountCompanyCommunitiesQuery,
  useGetBaoridersQuery,
  useCountCompanyProjectsQuery
} from 'generated/graphql'

import { OnboardingStep, OnboardingWizardProps } from './types'
import { usePreviewerContext } from 'context/PreviewerProvider'
import { useIsSubscribed } from 'hooks'
import { MdCheckCircle, MdRadioButtonUnchecked } from 'react-icons/md'
import PiaAvatar from 'components/UI/PiaAvatar'

const HelpWizard: React.FC<OnboardingWizardProps> = ({ customSteps }) => {
  const location = useLocation()
  const { pathname } = location
  const { data, loading } = useGetCompanyInfoQuery()

  const { data: projectData, refetch: refetchProjectData } = useCountCompanyProjectsQuery()
  const { data: dataCollectorCount, refetch: refetchDataCollectorCount } = useCountRidersQuery()
  // `$exists` is not a Strapi operator — it was silently ignored, so any
  // collector at all ticked "Add your Data Collector to a Project" off. A
  // collector is on a project when a linked project id is present.
  const { data: baoridersData, refetch: refetchBaoridersData } = useGetBaoridersQuery({
    variables: {
      pagination: { limit: 1, start: 0 },
      filters: { projects: { id: { $notNull: true } } }
    }
  })
  const { data: dataTaskCount, refetch: refetchDataTaskCount } = useCountTasksQuery()
  const { data: taskTypesCount, refetch: refetchTaskTypesCount } = useCountTaskTypesQuery()
  const { data: communitiesCount, refetch: refetchCommunitiesCount } =
    useCountCompanyCommunitiesQuery()
  const { setOnBoardingCompleted, onBoardingCompleted } = usePreviewerContext()
  const { isManual, isSubscribed } = useIsSubscribed()

  // Steps get completed on other pages, so re-check them on navigation — but
  // only while there is still a step left, and all at once. This panel is
  // mounted on every page, and it used to fire these six requests one after
  // another on every route change for every customer, forever.
  const isFirstPath = React.useRef(true)
  React.useEffect(() => {
    if (isFirstPath.current) {
      // The queries above have just run for this page.
      isFirstPath.current = false
      return
    }
    if (onBoardingCompleted) return

    Promise.all([
      refetchProjectData(),
      refetchDataCollectorCount(),
      refetchBaoridersData(),
      refetchDataTaskCount(),
      refetchTaskTypesCount(),
      refetchCommunitiesCount()
    ]).catch(() => undefined)
  }, [pathname]) // eslint-disable-line react-hooks/exhaustive-deps

  const onboardWizardList = React.useMemo(() => {
    return [
      {
        name: 'companyDetails',
        text: 'Complete your Company Details',
        url: '/auth/settings',
        openInNewTab: false,
        description: 'Provide information about your company and its goals'
      },
      {
        name: 'subscriptionDetails',
        text: (
          <span>
            Complete your <b>Subscription Details</b>
          </span>
        ),
        url: '/auth/settings',
        openInNewTab: false,
        description: 'This is the first step to get started with the platform'
      },
      {
        name: 'exploreProjects',
        text: 'Explore & Create a Project',
        url: '/auth/projects',
        openInNewTab: false,
        description: 'Create a project to start collecting data'
      },
      {
        name: 'dataCollector',
        text: 'Add a Data Collector',
        url: '/auth/baoriders',
        openInNewTab: false,
        description: 'Add a data collector to your project'
      },
      {
        name: 'addToProject',
        text: 'Add your Data Collector to a Project so they can collect Data on your Tasks',
        url: '/auth/projects',
        openInNewTab: false,
        description: 'Add your data collector to a project, so they can start collecting data'
      },
      {
        name: 'taskBuilder',
        text: 'Get to Know the Template Builder & Create a Template for your Project',
        url: '/auth/task-types-list',
        openInNewTab: false,
        description: 'Once you have a project, create a task template to start collecting data'
      },
      {
        name: 'createGroup',
        text: 'Create a Team for your Data Collectors',
        url: '/auth/communities',
        openInNewTab: false,
        description: 'Create a team for your data collectors'
      },

      {
        name: 'submitTask',
        text: 'Submit your First Task on the Mobile App & View it in the Task Table',
        url: '/auth/tasks',
        openInNewTab: false,
        description: 'Get your data collector to submit a task, then view it in the task table'
      }
    ]
  }, [isManual, pathname])

  const createWizardLink = (
    name = '',
    text: React.ReactNode = '',
    url = '#',
    openInNewTab = true,
    description = ''
  ): OnboardingStep => ({
    name,
    content: (
      <ChakraLink
        as={ReactRouterLink as any}
        target={openInNewTab ? '_blank' : '_self'}
        color="gray.900"
        to={typeof url === 'string' ? url : '#'}
      >
        {text || ''}
      </ChakraLink>
    ),
    description,
    pathname: typeof url === 'string' ? url : '#'
  })

  const defaultSteps: OnboardingStep[] = onboardWizardList.map((item) =>
    createWizardLink(item.name, item.text, item.url, item.openInNewTab, item.description)
  )
  const determineStepCompletion = (stepName: string): boolean => {
    // unique named ids for each step as the path might be the same for multiple steps
    switch (stepName) {
      case 'subscriptionDetails':
        return !!isSubscribed || isManual
      case 'exploreProjects':
        return (projectData?.countCompanyProjects || 0) > 0
      case 'companyDetails':
        return !!data?.getCompanyInfo?.name
      case 'taskBuilder':
        return !!taskTypesCount?.getCompanyTaskTypesCount
      case 'createGroup':
        return !!(
          communitiesCount?.countCompanyCommunities && communitiesCount?.countCompanyCommunities > 0
        )
      case 'dataCollector':
        return !!(dataCollectorCount?.countRiders && dataCollectorCount.countRiders > 0)
      case 'addToProject':
        return !!((baoridersData?.getBaoriders?.length ?? 0) > 0)
      case 'submitTask':
        return !!((dataTaskCount?.countTasks ?? 0) > 0)
      default:
        return !!data?.getCompanyInfo?.onboardingInformation?.[stepName]
    }
  }

  const finalSteps = customSteps ? (customSteps as OnboardingStep[]) : defaultSteps

  const defaultValues = React.useMemo(() => {
    return defaultSteps.reduce(
      (acc, step) => {
        // initialize based on saved onboarding info or the results of the step completion check?
        acc[step.name] = data?.getCompanyInfo?.onboardingInformation?.[step.name]
          ? data?.getCompanyInfo?.onboardingInformation?.[step.name]
          : determineStepCompletion(step.name)
        return acc
      },
      {} as Record<string, boolean>
    )
  }, [
    dataCollectorCount?.countRiders,
    dataTaskCount?.countTasks,
    taskTypesCount?.getCompanyTaskTypesCount,
    communitiesCount?.countCompanyCommunities,
    projectData?.countCompanyProjects,
    baoridersData?.getBaoriders,
    loading,
    isSubscribed,
    isManual,
    pathname
  ])

  React.useEffect(() => {
    if (!onBoardingCompleted && defaultValues) {
      const isAllStepsCompleted = finalSteps.every((step) => defaultValues[step.name])
      if (isAllStepsCompleted) {
        setOnBoardingCompleted?.(true)
      }
    }
  }, [defaultValues, onBoardingCompleted])

  const initialWizardList = (values: Record<string, any>) => {
    return (
      <Stack spacing={0}>
        {[...finalSteps]
          .sort((a, b) => {
            const aComplete = values[a.name] ? 1 : 0
            const bComplete = values[b.name] ? 1 : 0
            return bComplete - aComplete
          })
          .map((step, index) => {
            let currentStep
            if (!values[step.name]) {
              currentStep = defaultValues[step.name]
            } else {
              currentStep = values[step.name]
            }
            currentStep = values[step.name]
            const isLast = index === finalSteps.length - 1

            return (
              <HStack key={index} alignItems="stretch" spacing={3}>
                {/* Step indicator column */}
                <VStack spacing={0} alignItems="center" w="20px" flexShrink={0}>
                  <Center
                    w="20px"
                    h="20px"
                    borderRadius={0}
                    border="1px solid"
                    borderColor={!!currentStep ? 'gray.300' : 'gray.100'}
                    bg="white"
                    flexShrink={0}
                  >
                    <Icon
                      boxSize={3.5}
                      color={!!currentStep ? 'gray.400' : 'gray.300'}
                      as={!!currentStep ? MdCheckCircle : MdRadioButtonUnchecked}
                    />
                  </Center>
                  {!isLast && (
                    <Box
                      w="1px"
                      flex={1}
                      minH="16px"
                      bg={!!currentStep ? 'gray.200' : 'gray.100'}
                    />
                  )}
                </VStack>

                {/* Step content */}
                <Box pb={isLast ? 0 : 4} pt={0.5}>
                  <ChakraLink
                    as={ReactRouterLink as any}
                    to={step.pathname}
                    fontSize="xs"
                    lineHeight="1.3"
                    textDecoration={!!currentStep ? 'line-through' : 'none'}
                    color={
                      step.pathname === pathname
                        ? 'gray.900'
                        : !!currentStep
                          ? 'gray.400'
                          : 'gray.700'
                    }
                    fontWeight={step.pathname === pathname ? '500' : 'normal'}
                    _hover={{ color: 'gray.900' }}
                    transition="color 0.15s"
                  >
                    {step.content}
                  </ChakraLink>
                </Box>
              </HStack>
            )
          })}
      </Stack>
    )
  }

  return (
    <Box>
      {/* PIA hands over the checklist rather than a disembodied instruction,
          and this panel is where most people meet her first. */}
      <HStack mb={5} spacing={2.5} alignItems="flex-start">
        <PiaAvatar size={32} decorative />
        <Text fontSize="xs" color="gray.500" lineHeight="1.5" pt={1}>
          I&apos;m PIA. Work through these steps and you&apos;ll have Baotree set up.
        </Text>
      </HStack>

      {/* `defaultValues` is each step's saved flag OR its live check. Rendering the
          saved onboardingInformation alone ignored the live checks the moment any
          onboarding info existed — so a customer on a trial never saw
          "Subscription Details" ticked. */}
      {initialWizardList(defaultValues)}
    </Box>
  )
}

export default HelpWizard
